import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { useCoarsePointer } from "@/hooks/useCoarsePointer";
import { trackVideoProgress } from "@/lib/analytics";

export const ProductFrame = ({
  className = "",
  eventId = "hero",
  priority = false,
}) => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const reduced = usePrefersReducedMotion();
  const coarse = useCoarsePointer();
  const disableTilt = reduced || coarse;

  const mvX = useMotionValue(0);
  const mvY = useMotionValue(0);
  const rotateX = useSpring(useTransform(mvY, [-0.5, 0.5], [4, -4]), {
    stiffness: 120,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(mvX, [-0.5, 0.5], [-4, 4]), {
    stiffness: 120,
    damping: 18,
  });

  const handleMouseMove = (e) => {
    if (disableTilt || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    mvX.set((e.clientX - rect.left) / rect.width - 0.5);
    mvY.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleMouseLeave = () => {
    mvX.set(0);
    mvY.set(0);
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const pct = (v.currentTime / v.duration) * 100;
    if (pct >= 99) trackVideoProgress(eventId, 100);
    else if (pct >= 75) trackVideoProgress(eventId, 75);
    else if (pct >= 50) trackVideoProgress(eventId, 50);
    else if (pct >= 25) trackVideoProgress(eventId, 25);
  };

  return (
    <div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`relative ${className}`}
      style={disableTilt ? undefined : { perspective: 1600 }}
    >
      <div
        aria-hidden="true"
        className="absolute -inset-x-10 -inset-y-16 -z-10 blur-3xl opacity-[0.09] pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, #176B4D, transparent 70%)",
        }}
      />
      <motion.div
        style={disableTilt ? undefined : { rotateX, rotateY }}
        transition={{ type: "tween" }}
        className="relative mx-auto w-full max-w-[262px] rounded-[1.6rem] border border-[#171717]/8 bg-[#0B0C0E] p-1.5 shadow-[0_30px_80px_-20px_rgba(23,23,23,0.18)]"
        data-testid="product-video-frame"
      >
        <div className="relative overflow-hidden rounded-[1.2rem] bg-black">
          <video
            ref={videoRef}
            className="block w-full h-auto"
            src="/media/product-recording.mp4"
            poster="/media/product-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload={priority ? "auto" : "metadata"}
            onPlay={() => trackVideoProgress(eventId, 0)}
            onTimeUpdate={handleTimeUpdate}
            aria-label="Quantify product screen recording showing food logging and a personalised report"
            data-testid="product-video"
          />
        </div>
      </motion.div>
    </div>
  );
};
